import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { Building2, ChevronLeft, Eye, MoreVertical, Phone, Send } from 'lucide-react';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import toast from 'react-hot-toast';
import { createWhatsAppTemplate, getWhatsAppCampaigns } from '../services/api/implementation/impl';

const ACCENT = '#346C75';
/** WhatsApp Web–style app bar (current brand teal) */
const WA_APP_BAR = '#008069';
const WA_CHAT_BG = '#E5DDD5';
/** Light doodle-style wallpaper (SVG tiles, low contrast) */
const WA_WALLPAPER_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
<rect fill="#E5DDD5" width="120" height="120"/>
<g fill="#000" opacity="0.04">
<circle cx="12" cy="18" r="2"/><circle cx="58" cy="42" r="1.5"/><circle cx="96" cy="12" r="1.5"/>
<circle cx="32" cy="78" r="1.5"/><circle cx="88" cy="92" r="2"/><circle cx="18" cy="102" r="1.5"/>
<path d="M72 24h8v8h-8zM40 52h6v6h-6zM84 58h5v5h-5zM14 44h4v4h-4z"/>
</g>
</svg>`
);
const WA_WALLPAPER = `url("data:image/svg+xml,${WA_WALLPAPER_SVG}")`;
const WA_BUBBLE = '#FFFFFF';
const WA_META = '#667781';
const WA_QUICK_REPLY_TEXT = '#027EB5';
const WA_PREVIEW_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';
const PAGE_BG = '#F8F9FA';

/** ~6–7 lines at 14.2px / 1.47 line-height — collapsed body height in preview */
const PREVIEW_BODY_COLLAPSED_PX = 136;
/** Fixed chat viewport inside the phone mockup (scrolls when content is tall) */
const PHONE_CHAT_VIEWPORT_PX = 440;

/** Aggressive wrap + width bound so unbroken strings cannot expand the bubble (all browsers). */
const previewWrapStyle = {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  maxWidth: '100%'
};

function previewBlockClass(extra = '') {
  return `min-w-0 w-full max-w-full whitespace-pre-wrap ${extra}`.trim();
}

function normalizeTemplateName(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function normalizeCampaignStatus(raw) {
  const s = String(raw || '').trim().toLowerCase();
  return ['draft', 'active', 'completed'].includes(s) ? s : '';
}

function buildMarketingTemplatePayload(form) {
  const components = [];
  if (form.headerText.trim()) {
    components.push({ type: 'HEADER', format: 'TEXT', text: form.headerText.trim() });
  }
  const body = form.bodyText.trim();
  if (!body) {
    throw new Error('Message body is required.');
  }
  components.push({ type: 'BODY', text: body });
  if (form.footerText.trim()) {
    components.push({ type: 'FOOTER', text: form.footerText.trim() });
  }
  const qr = form.quickReplies.map((t) => t.trim()).filter(Boolean);
  if (qr.length) {
    components.push({
      type: 'BUTTONS',
      buttons: qr.map((text) => ({ type: 'QUICK_REPLY', text }))
    });
  }
  const name = normalizeTemplateName(form.templateName);
  if (!name) {
    throw new Error('Template name is required (letters, numbers, underscores).');
  }
  return {
    name,
    category: form.category,
    language: form.language,
    components
  };
}

function TemplatePhonePreview({ headerText, bodyText, footerText, quickReplies }) {
  const chips = quickReplies.map((t) => t.trim()).filter(Boolean);
  const bubbleShadow = '0 1px 0.5px rgba(11,20,26,0.13), 0 1px 1px rgba(11,20,26,0.08)';
  const previewTime = useMemo(
    () =>
      new Date().toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
    []
  );

  const bodyRef = useRef(null);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [bodyTruncatable, setBodyTruncatable] = useState(false);

  useEffect(() => {
    setBodyExpanded(false);
  }, [bodyText]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    const text = bodyText.trim();
    if (!el || !text) {
      setBodyTruncatable(false);
      return;
    }
    if (bodyExpanded) return;
    setBodyTruncatable(el.scrollHeight > el.clientHeight + 2);
  }, [bodyText, bodyExpanded]);

  return (
    <div className="mx-auto w-full max-w-[300px]">
      <div
        className="overflow-hidden rounded-[2rem] border-[10px] border-gray-900 bg-gray-900 shadow-2xl"
        style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)' }}>
        {/* WhatsApp-style chat header */}
        <div
          className="flex items-center gap-2 px-1.5 py-2 pr-2"
          style={{ backgroundColor: WA_APP_BAR, fontFamily: WA_PREVIEW_FONT }}>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/95 hover:bg-white/10"
            aria-label="Back (preview only)">
            <ChevronLeft className="h-6 w-6" strokeWidth={2} />
          </button>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
            <Building2 className="h-[18px] w-[18px] text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1 pr-1">
            <p className="truncate text-[15px] font-medium leading-tight text-white">Business</p>
            <p className="truncate text-[11px] leading-tight text-white/80">Template preview</p>
          </div>
          <div className="flex shrink-0 items-center text-white/90">
            <span className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10">
              <Phone className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10">
              <MoreVertical className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
          </div>
        </div>

        <div
          className="flex shrink-0 flex-col"
          style={{
            height: PHONE_CHAT_VIEWPORT_PX,
            minHeight: PHONE_CHAT_VIEWPORT_PX,
            maxHeight: PHONE_CHAT_VIEWPORT_PX,
            backgroundColor: WA_CHAT_BG,
            backgroundImage: `${WA_WALLPAPER}, ${WA_WALLPAPER}`,
            backgroundSize: '120px 120px',
            backgroundPosition: '0 0, 60px 60px',
            fontFamily: WA_PREVIEW_FONT
          }}>
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-2.5 pb-4 pt-3">
            <div className="flex w-full min-w-0 items-end justify-start gap-0">
              {/* Incoming bubble tail (WhatsApp Web–style) */}
              <div
                className="mb-[5px] h-0 w-0 shrink-0 border-y-[7px] border-y-transparent border-r-[9px]"
                style={{ borderRightColor: WA_BUBBLE }}
                aria-hidden
              />
              <div
                className="min-w-0 max-w-[min(100%,252px)] flex-1 overflow-hidden rounded-[10px] rounded-bl-[3px] bg-white"
                style={{ boxShadow: bubbleShadow, ...previewWrapStyle, backgroundColor: WA_BUBBLE }}>
                {headerText.trim() ? (
                  <div
                    className={previewBlockClass(
                      'border-b border-black/[0.06] bg-[#FAFAFA] px-2.5 pb-2 pt-2.5 text-[15px] font-semibold leading-snug text-[#111B21]'
                    )}
                    style={previewWrapStyle}>
                    {headerText}
                  </div>
                ) : null}
                <div>
                  {bodyText.trim() ? (
                    <div className="px-2.5 pt-2.5">
                      <div className="relative">
                        <div
                          ref={bodyRef}
                          className={previewBlockClass(
                            'pb-1 text-[14.2px] leading-[1.47] text-[#111B21]'
                          )}
                          style={{
                            ...previewWrapStyle,
                            ...(!bodyExpanded
                              ? { maxHeight: PREVIEW_BODY_COLLAPSED_PX, overflow: 'hidden' }
                              : {})
                          }}>
                          {bodyText}
                        </div>
                        {!bodyExpanded && bodyTruncatable ? (
                          <div
                            className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white via-white/80 to-transparent"
                            aria-hidden
                          />
                        ) : null}
                      </div>
                      {bodyTruncatable ? (
                        <button
                          type="button"
                          onClick={() => setBodyExpanded((v) => !v)}
                          className="w-full pb-1 pt-1 text-left text-[13px] font-medium text-[#027EB5] hover:underline">
                          {bodyExpanded ? 'Read less' : 'Read more'}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div
                      className={previewBlockClass(
                        'px-2.5 py-4 text-[14.2px] leading-[1.47] text-[#8696A0]'
                      )}
                      style={previewWrapStyle}>
                      <span className="italic">Your message will appear here…</span>
                    </div>
                  )}
                </div>
                {footerText.trim() ? (
                  <div
                    className={previewBlockClass(
                      'border-t border-black/[0.06] px-2.5 py-2 text-[12.5px] leading-snug text-[#8696A0]'
                    )}
                    style={previewWrapStyle}>
                    {footerText}
                  </div>
                ) : null}
                <div className="flex justify-end px-2 pb-2 pt-0.5">
                  <span
                    className="pl-2 text-[11px] tabular-nums leading-none"
                    style={{ color: WA_META }}>
                    {previewTime}
                  </span>
                </div>
                {/* Quick replies: same card as template text, like WhatsApp interactive templates */}
                {chips.length > 0 ? (
                  <div
                    className="divide-y divide-[#E9EDEF] border-t border-[#E9EDEF]"
                    style={{ fontFamily: WA_PREVIEW_FONT }}>
                    {chips.map((label, i) => (
                      <button
                        key={`${label}-${i}`}
                        type="button"
                        className="flex w-full min-w-0 items-center justify-center bg-transparent px-3 py-2.5 text-center text-[14px] font-normal leading-snug text-[#027EB5] transition-colors hover:bg-black/[0.04] active:bg-black/[0.06]"
                        style={{ ...previewWrapStyle, color: WA_QUICK_REPLY_TEXT }}>
                        {label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-3 text-center text-[11px] leading-relaxed text-gray-400">
        Approximate WhatsApp layout — final rendering may vary by client.
      </p>
    </div>
  );
}

const defaultForm = () => ({
  templateName: '',
  category: 'MARKETING',
  language: 'en_US',
  headerText: '',
  bodyText: '',
  footerText: '',
  quickReplies: ['Interested', 'Not interested']
});

const WhatsAppLeadFlowEditor = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { campaignId } = useParams();
  const location = useLocation();
  const { collapsed } = useSidebar();
  const userId = Cookies.get('userId');

  const [form, setForm] = useState(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const whatsappCampaignsBasePath = useMemo(
    () =>
      location.pathname.includes('/builder/marketing/')
        ? '/builder/marketing/whatsapp-campaigns'
        : '/builder/sales/whatsapp-campaigns',
    [location.pathname]
  );

  const campaignName = useMemo(() => {
    const fromState = location.state?.campaignName;
    if (fromState) return fromState;
    if (campaignId) return decodeURIComponent(String(campaignId).replace(/\+/g, ' '));
    return 'Campaign';
  }, [location.state, campaignId]);

  const campaignStatusFromState = useMemo(
    () => normalizeCampaignStatus(location.state?.campaignStatus),
    [location.state]
  );

  const { data: campaignStatusFromApi = '' } = useQuery({
    queryKey: ['whatsappCampaignStatus', campaignId],
    queryFn: async () => {
      const rows = await getWhatsAppCampaigns();
      const match = Array.isArray(rows)
        ? rows.find((row) => String(row.id) === String(campaignId))
        : null;
      return normalizeCampaignStatus(match?.status);
    },
    enabled: Boolean(campaignId) && !campaignStatusFromState
  });

  const campaignStatus = campaignStatusFromState || campaignStatusFromApi;
  const isDraftCampaign = campaignStatus === 'draft';

  useEffect(() => {
    mixpanel.track('WhatsApp Lead Flow Editor Viewed', {
      user_id: userId,
      campaign_id: campaignId,
      timestamp: new Date().toISOString()
    });
  }, [userId, campaignId]);

  const previewPayload = useMemo(() => {
    try {
      return buildMarketingTemplatePayload(form);
    } catch {
      return null;
    }
  }, [form]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleCreateTemplate = useCallback(async () => {
    if (!isDraftCampaign) {
      toast.error('Template can be created only when campaign is in Draft status.');
      return;
    }
    if (!campaignId) {
      toast.error('Missing campaign.');
      return;
    }
    const campaignNumericId = Number.parseInt(String(campaignId), 10);
    if (!Number.isFinite(campaignNumericId)) {
      toast.error('Invalid campaign id.');
      return;
    }
    let templatePayload;
    try {
      templatePayload = buildMarketingTemplatePayload(form);
    } catch (e) {
      toast.error(e.message || 'Check the form.');
      return;
    }
    const body = {
      whatsapp_campaign_id: campaignNumericId,
      name: templatePayload.name,
      category: templatePayload.category,
      language: templatePayload.language,
      components: templatePayload.components
    };
    setIsSubmitting(true);
    try {
      await createWhatsAppTemplate(body);
      mixpanel.track('WhatsApp Marketing Template Create Submitted', {
        user_id: userId,
        campaign_id: campaignId,
        template_name: templatePayload.name
      });
      toast.success('Template created successfully.');
      await queryClient.invalidateQueries({ queryKey: ['whatsappTemplates', campaignId] });
      const id = encodeURIComponent(String(campaignId));
      navigate(`${whatsappCampaignsBasePath}/${id}/questionnaire`, {
        replace: true,
        state: {
          campaignName: location.state?.campaignName ?? campaignName,
          campaignStatus
        }
      });
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not create template.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    campaignId,
    campaignName,
    campaignStatus,
    form,
    isDraftCampaign,
    location.state?.campaignName,
    navigate,
    queryClient,
    userId,
    whatsappCampaignsBasePath
  ]);

  const inputClass =
    'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition-shadow placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-[#346C75]/35';

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow
          onBack={() => navigate(whatsappCampaignsBasePath)}
          data={{
            name: campaignName,
            subtitle: 'WhatsApp marketing template'
          }}
        />
      </div>

      <div className="mt-[4.75rem] min-h-[calc(100vh-4.75rem)] pb-16" style={{ backgroundColor: PAGE_BG }}>
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-lg font-bold text-gray-900">Marketing message template</h1>
                <p className="text-sm text-gray-500">
                  {isDraftCampaign
                    ? 'Fill in the fields below. The preview updates as you type.'
                    : 'Campaign is not in draft. Template is in read-only mode.'}
                </p>
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px]">
              <div className="space-y-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="tpl-name">
                    Template name
                  </label>
                  <input
                    id="tpl-name"
                    type="text"
                    value={form.templateName}
                    onChange={(e) => setField('templateName', e.target.value)}
                    disabled={!isDraftCampaign}
                    placeholder="e.g. doctor_outreach_q2"
                    className={inputClass}
                  />
                  <p className="mt-1 text-[11px] text-gray-400">
                    Use a stable internal name (lowercase, underscores) when you wire up save later.
                  </p>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="tpl-header">
                    Header <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    id="tpl-header"
                    type="text"
                    value={form.headerText}
                    onChange={(e) => setField('headerText', e.target.value)}
                    disabled={!isDraftCampaign}
                    placeholder="Short headline"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="tpl-body">
                    Body <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="tpl-body"
                    value={form.bodyText}
                    onChange={(e) => setField('bodyText', e.target.value)}
                    disabled={!isDraftCampaign}
                    placeholder="Hi, are you interested in…"
                    rows={5}
                    className={`${inputClass} resize-y min-h-[120px]`}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="tpl-footer">
                    Footer <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    id="tpl-footer"
                    type="text"
                    value={form.footerText}
                    onChange={(e) => setField('footerText', e.target.value)}
                    disabled={!isDraftCampaign}
                    placeholder="Company name or disclaimer"
                    className={inputClass}
                  />
                </div>

                <div className="flex flex-wrap gap-3 border-t border-gray-100 pt-5">
                  {isDraftCampaign ? (
                    <button
                      type="button"
                      onClick={handleCreateTemplate}
                      disabled={isSubmitting}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 sm:flex-none sm:min-w-[180px]"
                      style={{ backgroundColor: ACCENT }}>
                      <Send className="h-4 w-4 shrink-0" strokeWidth={2} />
                      {isSubmitting ? 'Submitting…' : 'Create template'}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="lg:sticky lg:top-24 lg:self-start">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <Eye className="h-4 w-4 text-[#346C75]" strokeWidth={2} />
                  Preview
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <TemplatePhonePreview
                    headerText={form.headerText}
                    bodyText={form.bodyText}
                    footerText={form.footerText}
                    quickReplies={form.quickReplies}
                  />
                </div>
                {previewPayload ? (
                  <p className="mt-3 text-center text-[11px] text-gray-400">
                    {previewPayload.components.length} component
                    {previewPayload.components.length !== 1 ? 's' : ''} in payload
                  </p>
                ) : null}
              </div>
            </div>
        </div>
      </div>
    </Layout>
  );
};

export default WhatsAppLeadFlowEditor;
